import axios from "axios";
import type { InternalAxiosRequestConfig, AxiosError, AxiosResponse, AxiosInstance } from "axios";
import { QueryClient } from "@tanstack/react-query";
import { HTTP_STATUS } from "@constants/http.status.code";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { AUTH_ENDPOINTS, DEFAULT_TIMEOUT } from "@constants/api.routes";
import { clearAuthData, getAuthToken } from "@utils/auth";
import { store } from "@store/store"; // Do not import store here to avoid circular deps
import { clearUser } from "@store/slices/userSlice";
import { SERVER_URL } from "@config/env";
import { showNotification } from "@libs/notistack/notificationStack";
import { getTranslation } from "@utils/translation";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import type { ConfigErrorResponse } from "./types/types";

let isUnauthorizedHandled = false;
let lastErrorNotification = { message: "", timestamp: 0 };
const ERROR_DEDUP_INTERVAL = 2000; // 2 seconds

export const PORTAL_TYPE = "committee_member";

const HEADERS = {
	"Content-Type": "application/json",
	Accept: "application/json",
} as const;

export const HEADERS_MULTIPART = {
	"Content-Type": "multipart/form-data",
} as const;

export const baseConfig = {
	baseURL: SERVER_URL,
	timeout: DEFAULT_TIMEOUT,
	headers: HEADERS,
};

// Create axios instances
export const createAxiosInstance = (config: Partial<InternalAxiosRequestConfig> = {}): AxiosInstance => {
	const instance = axios.create({
		...baseConfig,
		...config,
	});

	return instance;
};

// Create API instances
export const api = createAxiosInstance();
export const authApi = createAxiosInstance();

// Function to check if endpoint is an auth endpoint
const isAuthEndpoint = (url: string): boolean => {
	return AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

// Function to handle unauthorized access
const handleUnauthorized = (error: AxiosError) => {
	// Always show the notification for 401 errors
	showNotification({
		message:
			(error?.response?.data as { message: string })?.message ||
			getTranslation("psdaCommon.messages.error.api.unauthorized"),
		severity: SNACKBAR_SEVERITY.ERROR,
	});

	// Only clear auth data once to prevent multiple clearing
	if (isUnauthorizedHandled) {
		return; // Prevent multiple auth clearing
	}

	isUnauthorizedHandled = true;

	// Clear all auth data
	clearAuthData();
	store.dispatch(clearUser()); // Do not use store here
};

export const resetUnauthorizedFlag = () => {
	isUnauthorizedHandled = false;
};

// Request interceptors
const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
	// If unauthorized flag is set and this is not an auth endpoint, block the request
	if (isUnauthorizedHandled && !isAuthEndpoint(config.url || "")) {
		// Return a rejected promise to properly block the request
		return Promise.reject(new Error("Unauthorized - request blocked")) as never;
	}
	const token = getAuthToken();
	if (config.headers) {
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		config.headers["x-user-type"] = PORTAL_TYPE;
	}
	return config;
};

// Response interceptors
const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
	return response?.data || response;
};

const errorInterceptor = (error: AxiosError): Promise<never> => {
	// Deduplicate error notifications
	const errorMessage = (error?.response?.data as { message: string })?.message || "Unknown error";
	const now = Date.now();

	if (lastErrorNotification.message === errorMessage && now - lastErrorNotification.timestamp < ERROR_DEDUP_INTERVAL) {
		// Skip duplicate notification
		return Promise.reject(error);
	}

	// Update last error notification
	lastErrorNotification = { message: errorMessage, timestamp: now };

	if (error?.response) {
		const axiosError = error as AxiosError;
		const errorData = axiosError?.response?.data as ConfigErrorResponse;
		const statusCode = errorData?.statusCode || error?.response?.status;

		// Handle 401 Unauthorized (special case - handled by handleUnauthorized)
		if (statusCode === HTTP_STATUS.UNAUTHORIZED) {
			handleUnauthorized(error);
			return Promise.reject(error);
		}

		// Handle 503 Service Unavailable (special case - redirect to error page)
		if (statusCode === HTTP_STATUS.SERVICE_UNAVAILABLE) {
			showNotification({
				message:
					(error?.response?.data as { message: string })?.message ||
					getTranslation("psdaCommon.messages.error.api.serverUnavailable"),
				severity: SNACKBAR_SEVERITY.ERROR,
			});
			// Use window.location for navigation outside React components
			window.location.href = BROWSER_ROUTES.SERVER_ERROR;
			return Promise.reject(error);
		}

		// Handle all other HTTP error status codes
		let notificationMessage = "";
		let translationKey = "";

		switch (statusCode) {
			case HTTP_STATUS.BAD_REQUEST:
				translationKey = "psdaCommon.messages.error.api.badRequest";
				break;
			case HTTP_STATUS.FORBIDDEN:
				translationKey = "psdaCommon.messages.error.api.forbidden";
				break;
			case HTTP_STATUS.NOT_FOUND:
				translationKey = "psdaCommon.messages.error.api.nresourceNotFound";
				break;
			case HTTP_STATUS.CONFLICT:
				translationKey = "psdaCommon.messages.error.api.conflict";
				break;
			case HTTP_STATUS.UNPROCESSABLE_ENTITY:
				translationKey = "psdaCommon.messages.error.api.unprocessableEntity";
				break;
			case HTTP_STATUS.INTERNAL_SERVER_ERROR:
				translationKey = "psdaCommon.messages.error.api.internalServerError";
				break;
			default:
				// Handle any other status codes (4xx, 5xx)
				if (statusCode >= 400 && statusCode < 500) {
					translationKey = "Something went wrong. Please check your request again.";
				} else if (statusCode >= 500 && statusCode < 600) {
					translationKey = "psdaCommon.messages.error.api.serverError";
				} else {
					translationKey = "psdaCommon.messages.error.api.unknown";
				}
				break;
		}

		// Show notification with server message or fallback translation
		notificationMessage = (error?.response?.data as { message: string })?.message || getTranslation(translationKey);

		showNotification({
			message: notificationMessage,
			severity: SNACKBAR_SEVERITY.ERROR,
		});
	} else if (error?.request) {
		// Network error or no response received
		showNotification({
			message: getTranslation("psdaCommon.messages.error.api.noResponse"),
			severity: SNACKBAR_SEVERITY.ERROR,
		});
	} else if (error?.code === "ERR_NETWORK") {
		// Network error
		showNotification({
			message: getTranslation("psdaCommon.messages.error.api.serverUnavailable"),
			severity: SNACKBAR_SEVERITY.ERROR,
		});
		// Use window.location for navigation outside React components
		window.location.href = BROWSER_ROUTES.SERVER_ERROR;
	} else {
		// Other errors (timeout, etc.)
		showNotification({
			message: getTranslation("psdaCommon.messages.error.api.unknown"),
			severity: SNACKBAR_SEVERITY.ERROR,
		});
	}
	return Promise.reject(error);
};

// Apply interceptors
[api, authApi].forEach(instance => {
	instance.interceptors.request.use(requestInterceptor, error => Promise.reject(error));
	instance.interceptors.response.use(responseInterceptor, errorInterceptor);
});

// React Query integration
export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 1,
			refetchOnWindowFocus: false,
			// staleTime: 5 * 60 * 1000, // 5 minutes
		},
	},
});
